import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_AP_LIST,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {resolveByFields} from '../../core/fieldSearch';
import {readLookupPages} from '../../core/lookupPages';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';
import {
	accessPointId,
	normalizeAccessPointReference,
} from './reference';

/**
 * Resolve Access Point candidates from the authenticated Canvas API.
 * @param {object} params resolver parameters
 * @param {object} pageContext current Canvas page context
 * @param {object} envelope command cancellation context
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveAccessPoints(params = {}, pageContext = {}, envelope = {}) {
	const query = String(params.query || '').trim();
	// `browse` lists the scope instead of matching a name, so a miss can answer
	// with the Access Points that do exist. Named lookups are unchanged.
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const limit = Math.min(
		Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
		LOOKUP_LIMIT,
	);
	const url = `${ENDPOINT_URL}${GET_AP_LIST}?limit=${limit}` +
		`&offset=0&search=${encodeURIComponent(query)}` +
		(params.site_id
			? `&group=${encodeURIComponent(params.site_id)}`
			: '');
	const rows = await readLookupPages({
		preview: browse && !query,
		pageSize: limit,
		rowsFrom: responseRows,
		fetchPage: async (offset) => {
			const {data} = await axios.get(url.replace('&offset=0', `&offset=${offset}`), {
				signal: envelope.signal, timeout: 3000,
			});
			return decodeAgentResponse(data) || {};
		},
	});
	replaceResolvedEntities('ap', rows, accessPointId);
	return {
		candidates: resolveByFields({
			rows,
			normalizeRow: normalizeAccessPointReference,
			query,
			limit,
			searchFields: params.search_fields,
			rank: rankLookupCandidates,
			// Used only when the agent sends no field catalogue, so a lookup
			// issued by an older agent still finds an Access Point by the
			// identifiers it always could.
			fallbackValues: (candidate) => [
				candidate.name,
				candidate.serial_no,
				candidate.mac_address,
				candidate.ip_address,
				candidate.ipv6_address,
				candidate.entity_id,
			],
			fuzzy: params.fuzzy === true,
		}),
	};
}

/**
 * Return the retained Access Point row for navigation.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedAccessPoint(entityId) {
	return getResolvedEntity('ap', entityId);
}
